import { inject, Injectable } from '@angular/core';
import { Erzeugungsanlage, ErzeugungsanlageListItem } from '../../domain/model/erzeugungsanlage';
import { environment } from '../../../environments/environment';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ErzeugungsanlageFilter } from './erzeugungsanlage-filter';
import { Pageable } from '../../common/paging/pageable';
import { ErzeugungsanlagenPage } from '../../domain/utils/page';
import { Range } from '../../domain/utils/range';
import { HttpParamHelperService } from '../utils/http-param-helper.service';
import { Adresse } from '../../domain/model/adresse.model';
import { Geokoordinaten } from '../../domain/model/geokoordinaten.model';
import { AbregelungsinformationenTase2Adressen } from '../../domain/model/abregelungsinformationen-tase2-adressen.model';
import { AbregelungKopplungsTyp } from '../../domain/model/abregelung-kopplungs-typ.enum';
import {
    AbregelungsinformationenIec101104Adressen,
} from '../../domain/model/abregelungsinformationen-iec101104-adressen.model';
import { FunkrundsteuerEmpfaengerInformationen } from '../../domain/model/funkrundsteuer-empfeanger-informationen.model';
import { ErzeugungsanlageSnapshot } from '../../domain/model/erzeugungsanlage-snapshot';

@Injectable({
    providedIn: 'root',
})
export class ErzeugungsanlageService {

    private http = inject(HttpClient);

    public getErzeugungsanlagen(
        pageable?: Pageable,
        filter?: ErzeugungsanlageFilter,
    ): Observable<ErzeugungsanlagenPage<ErzeugungsanlageListItem>> {
        const params = this.toHttpParams(pageable, filter);
        return this.http.get<ErzeugungsanlagenPage<Erzeugungsanlage>>(environment.API_URL + '/erzeugungsanlage', { params }).pipe(
            map(value => this.mapErzeugungsanlagenPageToErzeugungsanlageListItemPage(value)),
        );
    }

    private mapErzeugungsanlagenPageToErzeugungsanlageListItemPage(
        page: ErzeugungsanlagenPage<Erzeugungsanlage>,
    ): ErzeugungsanlagenPage<ErzeugungsanlageListItem> {
        return {
            ...page,
            ...{
                content: page.content.map(e => this.mapErzeugungsanlageToErzeugungsanlageListItem(e, page.erzeugungsanlageIdToPlanwertCount)),
            },
        };
    }

    private mapErzeugungsanlageToErzeugungsanlageListItem(
        erzeugungsanlage: Erzeugungsanlage,
        erzeugungsanlageIdToPlanwertCount: Map<number, number>,
    ): ErzeugungsanlageListItem {
        const planwertCount = erzeugungsanlageIdToPlanwertCount[erzeugungsanlage.id];
        return {
            ...erzeugungsanlage,
            ...{
                hasPlanwert: planwertCount && planwertCount > 0,
            },
        };
    }

    /**
     * Gets an Erzeugungsanlage identified by its unique Erzeugungsanlage.dokumentenId
     * @param {string} dokumentenId
     * @returns {Observable<Erzeugungsanlage>}
     */
    public getErzeugungsanlage(dokumentenId: string): Observable<Erzeugungsanlage> {
        return this.http.get(environment.API_URL + '/erzeugungsanlage/' + dokumentenId)
            .pipe(
                map((erzeugungsanlage: Erzeugungsanlage) => {
                    if (erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenTase2) {
                        erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenTase2.adressen =
                            this.populateTaseAdressen(
                                erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenTase2.adressen,
                            );
                    }

                    if (erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenIEC101104) {
                        erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenIEC101104.adressen =
                            this.populateIECAdressen(
                                erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenIEC101104.adressen,
                            );
                    }
                    erzeugungsanlage.anlagenAnschrift = erzeugungsanlage.anlagenAnschrift || new Adresse();
                    erzeugungsanlage.geokoordinaten = erzeugungsanlage.geokoordinaten || Geokoordinaten.createUTM(32, null, null);
                    return erzeugungsanlage;
                }),
            );
    }

    public getInstallierteLeistungRange(stadtwerkeId?: string): Observable<Range> {
        const params = this.toHttpParams(undefined, { stadtwerkeId });

        return this.http.get<Range>(environment.API_URL + '/erzeugungsanlage/installierteLeistungRange', { params });
    }

    public createErzeugungsanlage(stadtwerkeId: string, erzeugungsanlage: Erzeugungsanlage): Observable<Erzeugungsanlage> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);

        return this.http.post<Erzeugungsanlage>(environment.API_URL + '/erzeugungsanlage', erzeugungsanlage, { params });
    }

    public saveErzeugungsanlage(stadtwerkeId: string, erzeugungsanlage: Erzeugungsanlage): Observable<Erzeugungsanlage> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);

        return this.http.put<Erzeugungsanlage>(environment.API_URL + '/erzeugungsanlage/' + erzeugungsanlage.dokumentenId, erzeugungsanlage, { params });
    }

    public checkErzeugungsanlageForPlanwertChanges(stadtwerkeId: string, erzeugungsanlage: Erzeugungsanlage): Observable<boolean> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);

        return this.http.put<{ hasPlanwertChanges: boolean }>(
            `${environment.API_URL}/erzeugungsanlage/${erzeugungsanlage.dokumentenId}/checkPlanwertChanges`,
            erzeugungsanlage,
            { params },
        ).pipe(
            map(value => value.hasPlanwertChanges),
        );
    }

    public deleteErzeugungsanlage(dokumentenId: string): Observable<any> {
        return this.http.delete(environment.API_URL + '/erzeugungsanlage/' + dokumentenId) as Observable<any>;
    }

    private toHttpParams(pageable?: Pageable, filter?: ErzeugungsanlageFilter): HttpParams {
        let params = new HttpParams({
            fromObject: pageable || {} as any,
        });

        if (filter) {
            if (filter.netzbetreiberName) {
                params = params.set('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                params = params.set('netzbetreiberTeilgebiet', filter.netzbetreiberTeilgebiet);
            }
            if (filter.stadtwerkeId) {
                params = params.set('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.dokumentenId) {
                params = params.set('dokumentenId', filter.dokumentenId);
            }
            if (filter.steuerbareResourceId) {
                params = params.set('steuerbareResourceId', filter.steuerbareResourceId);
            }
            if (filter.technischeResourceId) {
                params = params.set('technischeResourceId', filter.technischeResourceId);
            }
            if (filter.bezVnbIntern) {
                params = params.set('bezVnbIntern', filter.bezVnbIntern);
            }
            if (filter.ortsname) {
                params = params.set('ortsname', filter.ortsname);
            }
            if (filter.stationsname) {
                params = params.set('stationsname', filter.stationsname);
            }
            if (filter.trafoFeldName) {
                params = params.set('trafoFeldName', filter.trafoFeldName);
            }
            if (filter.energietraeger) {
                params = params.set('energietraeger', filter.energietraeger);
            }
            if (typeof filter.status == 'boolean') {
                params = params.set('status', JSON.stringify(filter.status));
            }
            if (typeof filter.installierteLeistungMin == 'number') {
                params = params.set('installierteLeistungMin', filter.installierteLeistungMin + '');
            }
            if (typeof filter.installierteLeistungMax == 'number') {
                params = params.set('installierteLeistungMax', filter.installierteLeistungMax + '');
            }
            if (filter.anlagenschluessel) {
                params = params.set('anlagenschluessel', filter.anlagenschluessel);
            }
        }

        return params;
    }

    public getDokumentenId(dokumentenId?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${environment.API_URL}/erzeugungsanlage/dokumentenId/${dokumentenId || ''}`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId }),
        });
    }

    public getSteuerbareResourceId(steuerbareResourceId?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${environment.API_URL}/erzeugungsanlage/steuerbareResourceId/${steuerbareResourceId || ''}`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId }),
        });
    }

    public getTechnischeResourceId(technischeResourceId?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${environment.API_URL}/erzeugungsanlage/technischeResourceId/${technischeResourceId || ''}`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId }),
        });
    }

    public getBezVnbIntern(bezVnbIntern?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${environment.API_URL}/erzeugungsanlage/bezVnbIntern/${bezVnbIntern || ''}`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId }),
        });
    }

    public isAnlagenschluesselUnique(anlagenschluessel?: string, dokumentenId?: string): Observable<boolean> {
        if (!anlagenschluessel) {
            return of(false);
        }

        let params = new HttpParams();

        params = params.set('anlagenschluessel', anlagenschluessel);
        if (dokumentenId) {
            params = params.set('dokumentenId', dokumentenId);
        }

        return this.http.get<boolean>(
            `${environment.API_URL}/erzeugungsanlage/isAnlagenschluesselUnique`,
            { params },
        );
    }

    private populateTaseAdressen(adressen?: AbregelungsinformationenTase2Adressen[]): AbregelungsinformationenTase2Adressen[] {
        const defaults: AbregelungsinformationenTase2Adressen[] = Object.values(AbregelungKopplungsTyp)
            .map(kopplungsTyp => {
                return { kopplungsTyp, tase2Objekt: null, elementTyp: null };
            });
        return this.populateAdressen(adressen, defaults);
    }

    private populateIECAdressen(adressen?: AbregelungsinformationenIec101104Adressen[]): AbregelungsinformationenIec101104Adressen[] {
        const defaults: AbregelungsinformationenIec101104Adressen[] = Object.values(AbregelungKopplungsTyp)
            .map(kopplungsTyp => {
                return { kopplungsTyp, hohesByte: null, mittleresByte: null, niedrigesByte: null, typkennung: null };
            });
        return this.populateAdressen(adressen, defaults);
    }

    private populateAdressen<T extends { kopplungsTyp: AbregelungKopplungsTyp }>(adressen: T[] | undefined, defaults: T[]): T[] {
        adressen?.forEach(adresse => {
            const index = defaults.findIndex(a => a.kopplungsTyp === adresse.kopplungsTyp);
            defaults[index] = adresse;
        });
        return defaults;
    }

    public getSnapshot(stadtwerkeId?: string): Observable<ErzeugungsanlageSnapshot> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        return this.http.get<ErzeugungsanlageSnapshot>(environment.API_URL + '/erzeugungsanlage/snapshot', { params });
    }

    public getSteuerbareResourceIdConflicts(
        steuerbareResourceId?: string,
        netzzuordnungId?: string,
        funkrundsteuerEmpfaengerInformationen?: FunkrundsteuerEmpfaengerInformationen,
        steuergruppeId?: string,
        stadtwerkeId?: string,
        dokumentenId?: string,
    ): Observable<string[]> {
        if (!steuerbareResourceId || !netzzuordnungId || !funkrundsteuerEmpfaengerInformationen) {
            return of([]);
        }

        let params = new HttpParams();

        params = params.set('steuerbareResourceId', steuerbareResourceId);
        params = params.set('netzzuordnungId', netzzuordnungId);
        if (steuergruppeId) {
            params = params.set('steuergruppeId', steuergruppeId);
        }
        if (dokumentenId) {
            params = params.set('dokumentenId', dokumentenId);
        }
        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        return this.http.post<string[]>(
            `${environment.API_URL}/erzeugungsanlage/steuerbareResourceIdConflicts`,
            funkrundsteuerEmpfaengerInformationen,
            { params },
        );
    }

    public isTechnischeResourceIdUnique(technischeResourceId?: string, dokumentenId?: string): Observable<boolean> {
        if (!technischeResourceId) {
            return of(false);
        }

        let params = new HttpParams();

        params = params.set('technischeResourceId', technischeResourceId);
        if (dokumentenId) {
            params = params.set('dokumentenId', dokumentenId);
        }

        return this.http.get<boolean>(
            `${environment.API_URL}/erzeugungsanlage/isTechnischeResourceIdUnique`,
            { params },
        );
    }

    public isSteuergruppeIdNetzzuordnungValid(
        steuergruppeId?: string,
        netzzuordnungId?: string,
        dokumentenId?: string,
    ): Observable<boolean> {
        if (!steuergruppeId || !netzzuordnungId) {
            return of(false);
        }

        let params = new HttpParams();

        params = params.set('steuergruppeId', steuergruppeId);
        params = params.set('netzzuordnungId', netzzuordnungId);
        if (dokumentenId) {
            params = params.set('dokumentenId', dokumentenId);
        }

        return this.http.get<boolean>(
            `${environment.API_URL}/erzeugungsanlage/isSteuergruppeIdAndNetzzuordnungValid`,
            { params },
        );
    }
}
